import { forwardRef } from 'react'
import {
  type ExtendButtonBase,
  type ButtonBaseTypeMap,
  CircularProgress,
  type ButtonBaseProps,
  type ButtonProps as MuiButtonProps,
} from '@mui/material'
import * as $ from './Button.styled'

export interface Props extends $.BaseButtonProps {
  /**
   * Props applied to the `StartIconBox` element.
   */
  startIconBoxProps?: React.ComponentProps<typeof $.StartIconBox>

  /**
   * Props applied to the `EndIconBox` element.
   */
  endIconBoxProps?: React.ComponentProps<typeof $.EndIconBox>

  /**
   * If `true`, the button becomes disabled and shows loading indicator as start icon.
   * @default false
   */
  loading?: boolean
  /**
   * Keeps the button clickable while `loading`, outranking `disabled`. Only pass it when the click handler cancels.
   */
  cancellable?: boolean
  /**
   * href prop from the MUI Button component.
   */
  href?: MuiButtonProps['href']
  target?: string
  rel?: string
  'data-testid'?: string
}

export type ButtonProps<D extends React.ElementType = 'button', P = unknown> = ButtonBaseProps<D, Props & P>

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = 'medium',
      startIcon,
      startIconBoxProps,
      endIcon,
      endIconBoxProps,
      kind = 'neutral',
      variant = 'secondary',
      children,
      fullWidth,
      loading,
      cancellable,
      disabled,
      active,
      ...otherProps
    },
    ref,
  ) => {
    const isCancellableLoading = Boolean(loading && cancellable)

    return (
      <$.Button
        endIcon={endIcon}
        startIcon={startIcon}
        kind={isCancellableLoading ? 'destructive' : kind}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        ref={ref}
        disabled={!isCancellableLoading && Boolean(loading || disabled)}
        aria-selected={active}
        active={active}
        {...otherProps}
      >
        {(startIcon || loading) && (
          <$.StartIconBox size={size} {...startIconBoxProps}>
            {loading ? <CircularProgress size={size === 'small' ? 16 : 20} color="inherit" /> : startIcon}
          </$.StartIconBox>
        )}
        {children}
        {endIcon && (
          <$.EndIconBox size={size} {...endIconBoxProps}>
            {endIcon}
          </$.EndIconBox>
        )}
      </$.Button>
    )
  },
) as ExtendButtonBase<ButtonBaseTypeMap<Props>> & Pick<React.FunctionComponent, 'displayName'>

Button.displayName = 'Button'
